import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { postsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const PostDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: '' });
  const [submitting, setSubmitting] = useState(false);

  const fetchPost = useCallback(async () => {
    setLoading(true);
    try {
      const response = await postsAPI.getById(id);
      setPost(response.data);
      
      // Fetch related posts (same category)
      const relatedResponse = await postsAPI.getAll({ 
        category: response.data.category,
        limit: 3 
      });
      setRelatedPosts(relatedResponse.data.filter(p => p.post_id !== parseInt(id)));
      
      setLoading(false);
    } catch (err) {
      setError('Failed to load post');
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchPost();
    window.scrollTo(0, 0);
  }, [fetchPost]);

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      alert('Please login to leave a review');
      return;
    }

    setSubmitting(true);
    try {
      await postsAPI.addReview(id, reviewForm);
      setReviewForm({ rating: 5, comment: '' });
      fetchPost(); 
    } catch (err) {
      alert('Failed to submit review');
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          <p className="mt-4 text-on-surface-variant font-medium font-body">Curating your experience...</p>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="max-w-md text-center">
          <span className="material-symbols-outlined text-6xl text-error mb-4">error</span>
          <h2 className="text-2xl font-bold text-on-surface mb-2 font-headline">{error || 'Post not found'}</h2>
          <button onClick={() => navigate('/explore')} className="mt-4 text-primary font-bold border-b-2 border-primary font-body">
            Back to Explore
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background text-on-surface selection:bg-primary-fixed selection:text-on-primary-fixed font-body antialiased">
      <main className="pt-24 pb-12">
        {/* Hero Section */}
        <section className="px-8 mb-20">
          <div className="relative h-[600px] md:h-[716px] w-full rounded-xl overflow-hidden group">
            <img 
              alt={post.title} 
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" 
              src={post.image_urls?.[0] || "https://images.unsplash.com/photo-1555990136-2d9340643920?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80"}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
            <div className="absolute bottom-12 left-12 right-12 flex flex-col md:flex-row md:items-end justify-between gap-6">
              <div>
                <span className="inline-block px-4 py-1.5 mb-4 rounded-full bg-primary-fixed text-on-primary-fixed text-sm font-semibold tracking-wide uppercase">
                  {post.category}
                </span>
                <h1 className="text-5xl md:text-7xl font-extrabold text-white tracking-tighter mb-2 font-headline">
                  {post.title}
                </h1>
                <div className="flex items-center text-white/90 gap-2">
                  <span className="material-symbols-outlined text-primary-fixed">location_on</span>
                  <span className="text-lg font-medium tracking-tight">{post.location_name}</span>
                </div>
              </div>
              <div className="flex gap-4">
                <button className="bg-primary-fixed hover:bg-primary text-on-primary-fixed hover:text-on-primary px-8 py-4 rounded-full font-bold transition-all flex items-center gap-2 shadow-xl active:scale-95">
                  <span className="material-symbols-outlined">bookmark</span>
                  Save to Guide
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Content Grid: Asymmetric Editorial Layout */}
        <section className="px-8 max-w-7xl mx-auto grid grid-cols-12 gap-12 lg:gap-24">
          
          {/* Left Column: The Narrative */}
          <div className="col-span-12 lg:col-span-7 space-y-20">
            
            {/* Curator's Note */}
            <div className="relative">
              <div className="absolute -left-4 top-0 w-1 h-24 bg-primary rounded-full"></div>
              <h2 className="text-sm font-bold uppercase tracking-[0.2em] text-primary mb-6">The Curator's Note</h2>
              <p className="text-3xl md:text-4xl font-light text-on-surface leading-relaxed tracking-tight font-headline">
                "{post.content}"
              </p>
              <div className="mt-10 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full overflow-hidden bg-primary-fixed flex items-center justify-center font-bold text-on-primary-fixed">
                  {post.full_name?.charAt(0) || 'A'}
                </div>
                <div>
                  <p className="font-bold text-on-surface">{post.full_name || 'Local Advisor'}</p>
                  <p className="text-sm text-on-surface-variant">Chief Cultural Curator</p>
                </div>
              </div>
            </div>

            {/* Curated Highlights: Bento Style */}
            <div>
              <h2 className="text-sm font-bold uppercase tracking-[0.2em] text-primary mb-8">Curated Highlights</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="p-8 bg-surface-container-low rounded-xl flex flex-col justify-between h-64 hover:bg-white transition-all shadow-sm group">
                  <span className="material-symbols-outlined text-4xl text-primary group-hover:scale-110 transition-transform">celebration</span>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Unique Vibe</h3>
                    <p className="text-on-surface-variant text-sm">Experience the authentic energy of Bucharest. This spot is a local favorite for its unmatched atmosphere.</p>
                  </div>
                </div>
                <div className="p-8 bg-surface-container-low rounded-xl flex flex-col justify-between h-64 hover:bg-white transition-all shadow-sm group">
                  <span className="material-symbols-outlined text-4xl text-secondary group-hover:scale-110 transition-transform">photo_camera</span>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Visual Heritage</h3>
                    <p className="text-on-surface-variant text-sm">A masterclass in {post.category === 'Nature' ? 'natural beauty' : 'urban character'}. Every angle here tells a story of the city.</p>
                  </div>
                </div>
                <div className="md:col-span-2 p-8 bg-primary text-on-primary rounded-xl flex flex-col md:flex-row items-center gap-8 shadow-xl">
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold mb-2">Community Wisdom</h3>
                    <p className="text-primary-fixed/80">Average community rating: <span className="text-white font-bold">{post.average_rating ? parseFloat(post.average_rating).toFixed(1) : 'New'} / 5.0</span>. Based on {post.reviews?.length || 0} local insights.</p>
                  </div>
                  <button className="bg-primary-fixed text-on-primary-fixed px-6 py-3 rounded-full font-bold whitespace-nowrap hover:scale-105 transition-transform active:scale-95">
                    View Reviews
                  </button>
                </div>
              </div>
            </div>

            {/* Community Section (Review Form & List) */}
            <div className="pt-20 border-t border-outline-variant/30">
              <h2 className="text-sm font-bold uppercase tracking-[0.2em] text-primary mb-8">Community Voices</h2>
              
              {isAuthenticated ? (
                <div className="bg-surface-container-low p-8 rounded-xl mb-12 shadow-sm">
                  <h3 className="text-xl font-bold mb-6">Add your insight</h3>
                  <div className="space-y-6">
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-widest text-on-surface-variant mb-2">Rating</label>
                      <div className="flex gap-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            onClick={() => setReviewForm({ ...reviewForm, rating: star })}
                            className={`material-symbols-outlined text-3xl transition-colors ${star <= reviewForm.rating ? 'text-yellow-500 fill-1' : 'text-outline-variant'}`}
                          >
                            star
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-widest text-on-surface-variant mb-2">Your experience</label>
                      <textarea
                        value={reviewForm.comment}
                        onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                        rows="4"
                        className="w-full bg-white border-none rounded-xl p-4 focus:ring-2 focus:ring-primary/20 text-on-surface"
                        placeholder="What makes this place special for you?"
                      />
                    </div>
                    <button
                      onClick={handleReviewSubmit}
                      disabled={submitting}
                      className="bg-primary text-on-primary px-8 py-3 rounded-full font-bold hover:opacity-90 disabled:opacity-50 transition-all active:scale-95"
                    >
                      {submitting ? 'Sharing...' : 'Share Review'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="bg-surface-container-low p-8 rounded-xl text-center mb-12 border border-dashed border-outline-variant">
                  <p className="text-on-surface-variant font-medium italic">Sign in to share your local secrets with the community.</p>
                </div>
              )}

              <div className="space-y-10">
                {post.reviews?.map((review) => (
                  <div key={review.review_id} className="flex gap-6 items-start animate-fade-in">
                    <div className="w-12 h-12 rounded-full overflow-hidden bg-primary-fixed shrink-0 flex items-center justify-center font-bold text-on-primary-fixed border-2 border-white shadow-sm">
                      {review.full_name.charAt(0)}
                    </div>
                    <div className="flex-grow">
                      <div className="flex items-center justify-between mb-2">
                        <h5 className="font-bold text-on-surface">{review.full_name}</h5>
                        <span className="text-xs text-on-surface-variant/60">{new Date(review.created_at).toLocaleDateString()}</span>
                      </div>
                      <div className="flex gap-0.5 mb-3">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <span key={star} className={`material-symbols-outlined text-sm ${star <= review.rating ? 'text-yellow-500 fill-1' : 'text-outline-variant'}`}>star</span>
                        ))}
                      </div>
                      <p className="text-on-surface-variant leading-relaxed text-sm">
                        {review.comment}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column: Practical & Contextual */}
          <div className="col-span-12 lg:col-span-5 space-y-12">
            
            {/* The Vibe */}
            <div className="p-10 bg-surface-container-lowest rounded-xl shadow-[0_32px_48px_rgba(18,28,42,0.04)] border border-outline-variant/10">
              <h2 className="text-sm font-bold uppercase tracking-[0.2em] text-on-surface-variant mb-6">The Vibe</h2>
              <div className="flex flex-wrap gap-3">
                {post.tags?.map((tag, idx) => (
                  <span key={idx} className="px-5 py-2.5 bg-surface-container text-on-surface rounded-full text-sm font-medium hover:bg-surface-container-high transition-colors">
                    {tag}
                  </span>
                ))}
                {!post.tags?.length && <span className="text-on-surface-variant italic text-sm">Curating the vibe tags...</span>}
              </div>
            </div>

            {/* Practical Info */}
            <div className="space-y-8 p-10 bg-white rounded-xl shadow-sm border border-outline-variant/5">
              <div className="flex items-start gap-4">
                <span className="material-symbols-outlined text-primary-container mt-1">schedule</span>
                <div>
                  <h4 className="font-bold mb-1">Opening Hours</h4>
                  <p className="text-on-surface-variant text-sm leading-relaxed">
                    Mon - Fri: 08:00 — 20:00<br/>
                    Sat - Sun: 09:00 — 22:00
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <span className="material-symbols-outlined text-primary-container mt-1">map</span>
                <div className="w-full">
                  <h4 className="font-bold mb-3">Location</h4>
                  <p className="text-sm text-on-surface-variant mb-4">{post.location_name}</p>
                  <div className="w-full h-48 rounded-xl bg-surface-container-high relative overflow-hidden group">
                    <img 
                      alt="Map" 
                      className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" 
                      src="https://images.unsplash.com/photo-1526778548025-fa2f459cd5c1?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center shadow-2xl animate-pulse">
                        <span className="material-symbols-outlined text-white">location_on</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <button className="w-full bg-surface-container text-on-surface py-4 rounded-full font-bold hover:bg-surface-container-high transition-all active:scale-95 flex items-center justify-center gap-2">
                <span className="material-symbols-outlined">directions</span>
                Get Directions
              </button>
            </div>
          </div>
        </section>

        {/* Related Places Section */}
        {relatedPosts.length > 0 && (
          <section className="mt-32 px-8 max-w-7xl mx-auto">
            <h2 className="text-3xl font-extrabold tracking-tighter mb-10 font-headline">Neighbors Worth Visiting</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {relatedPosts.map((related) => (
                <Link key={related.post_id} to={`/posts/${related.post_id}`} className="group cursor-pointer">
                  <div className="relative aspect-[4/5] rounded-xl overflow-hidden mb-4">
                    <img 
                      alt={related.title} 
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" 
                      src={related.image_urls?.[0] || "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-6">
                      <span className="text-white font-bold text-sm">Explore now</span>
                    </div>
                  </div>
                  <h3 className="text-xl font-bold tracking-tight group-hover:text-primary transition-colors">{related.title}</h3>
                  <p className="text-on-surface-variant text-sm">{related.category} • {related.location_name}</p>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
};

export default PostDetailPage;
